import React, { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDataProvider, useTranslate } from 'react-admin';
import { usePopupState, bindToggle } from "material-ui-popup-state/hooks";
import colors from "../CustomizedUIs/Colors";
import Search from "../search";
import TableTemplateHeader from './table-template-header';
import TableTemplateItem from './table-template-item';
import TablePagination from './table-pagination';
import Snackbar from '../common/snackbar';
import DialogCreateTemplate from './dialog-create-template';
import DialogEditTemplate from './dialog-edit-template';
import Loading from '../common/loading';

// Images
import icEmail from "../assets/icons/white-email-action-send.png";
import icChevronRight from "../assets/icons/icChevronRight.png";
import icChevronLeft from "../assets/icons/icChevronLeft.png";
import icRefresh from "../assets/icons/icRefresh.png";

const pagination = { page: 1, perPage: 25 };
let TABLE_SORT = { field: 'createdAt', order: 'DESC' };
const useStyles = makeStyles((theme) => ({
    selected: {
        backgroundColor: colors.lightBlue["900"]
    },
    button: {
        marginRight: 20,
        fontSize: 9
    },
    lastButton: {
        marginRight: 40,
        fontSize: 9
    },
    btnIconOnly: {
        width: 12,
        height: 12,
        padding: 6,
        border: 'solid 1.5px #e5f1f8',
        borderRadius: 7.5,
        cursor: 'pointer',

        '&:active': {
            opacity: 0.5
        }
    },
    dialogCloseBtn: {
        backgroundColor: theme.palette.common.white,
        marginLeft: 10
    },
    textCountPagination: {
        fontSize: 11,
        color: '#4f5064',
        fontFamily: 'UniviaPro-Medium',
        fontWeight: 500,
        letterSpacing: 'normal',
        lineHeight: '13.2px',
        fontStyle: 'normal',
        fontStretch: 'normal'
    }
}));

let timeoutDeleted = null;
function TabSend({ totalItems, setTotalItems, isCreate, isUpdate, isDelete }) {
    const classes = useStyles();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const popupState = usePopupState({ variant: 'popper', popupId: 'sendMessageDialog' });

    // STATES REGION
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [deleteTemplate, setDeleteTemplate] = useState('');
    const [order, setOrder] = useState({ field: 'updatedAt', order: 'DESC' });
    const [showSelectedHeader, setShowSelectedHeader] = useState([]);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [currentEditTemplate, setCurrentEditTemplate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [checkall, setCheckAll] = useState(false)
    // END STATES REGION

    useEffect(() => {
        getTemplates();
    }, []);

    const getTemplates = () => {
        setLoading(true);
        dataProvider('GET_LIST', 'message-templates', {
            filter: {
                or: [
                    { name: { contains: searchText.trim() } },
                    { subject: { contains: searchText.trim() } },
                    { message: { contains: searchText.trim() } }
                ]
            },
            pagination,
            sort: TABLE_SORT,
        }).then(({ data, total }) => {
            const newData = data.map(item => ({
                ...item,
                checked: false
            }));
            setTemplates(newData);
            setTotalItems(total);
            setLoading(false);

            calculateTotalPage(total, data.length);
        }).catch(error => {
            // TODO: Should show error message
            console.error('getTemplates error:', error)
            setLoading(false);
        });
    }

    const calculateTotalPage = (total, size) => {
        // CALCULATE TOTAL PAGES
        const totalPages = Math.ceil(total / pagination.perPage);
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(i);
        }
        setPages(pages);
    }

    const onPrevious = () => {
        if (page > 1) {
            onChangePage(page - 1);
        }
    }

    const onNext = () => {
        if (page < pages.length) {
            onChangePage(page + 1);
        }
    }

    const onChangePage = (nextPage) => {
        console.log('ON Change page:', nextPage);
        if (nextPage !== page) {
            setPage(nextPage);
            pagination.page = nextPage;
            // TODO: Call api
            getTemplates();
        }
    }

    const onRefresh = () => {
        setTemplates([]);
        getTemplates();
    }

    const onUndoDeleted = () => {
        clearTimeout(timeoutDeleted);
        setDeleteTemplate('');
        getTemplates();
    }

    const onChangeSortable = (nextSortable) => {
        TABLE_SORT = nextSortable;
        setOrder(TABLE_SORT);
        getTemplates();
    }

    const onCheckAll = (e) => {
        const newTemplates = templates.map(item => {
            if (checkall === false) {
                return {
                    ...item,
                    checked: true
                }
            }
            else {
                return {
                    ...item,
                    checked: false
                }
            }

        });
        const selectedItems = newTemplates.filter(item => item.checked);
        const isCheckAll = selectedItems.length === newTemplates.length;
        setCheckAll(isCheckAll);

        setTemplates(newTemplates);
        setShowSelectedHeader(selectedItems);
    }

    const onDeleteMany = () => {
        const ids = templates.filter(item => item.checked).map(item => item.id);
        const newTemplates = templates.filter(item => !item.checked);
        const msgText = ids.length === 1 ? '1 file deleted!' : ids.length + ' files deleted!';
        setShowSelectedHeader([]);
        setTemplates(newTemplates);
        setDeleteTemplate(msgText);

        timeoutDeleted = setTimeout(() => {
            setDeleteTemplate('');
            dataProvider('DELETE_MANY', 'message-template', {
                ids,
            }).then(res => {
                console.log('On Delete templates:', res);
                if (templates.length === ids.length) {
                    if (page === pages.length && page !== 1) {
                        pagination.page = page - 1;
                        setPage(pagination.page);
                    }
                }
                getTemplates();
            }).catch(error => {
                console.error('On Delete:', error);
            });
        }, 6000);
    }

    const onCheck = (id) => {
        const newTemplates = templates.map(item => {
            return {

                ...item,
                checked: item.id === id ? !item.checked : item.checked
            }

        });

        let isCheckAll = newTemplates.filter(item => item.checked).length === templates.length;
        setCheckAll(isCheckAll);
        setTemplates(newTemplates)
        const newSelectedItems = newTemplates.filter(item => item.checked);
        setShowSelectedHeader(newSelectedItems);
    }



    const onDeleteOne = (id) => {
        const newTemplates = templates.filter(item => item.id !== id);
        setTemplates(newTemplates);
        setDeleteTemplate('1 file deleted!');
        setShowSelectedHeader([]);

        timeoutDeleted = setTimeout(() => {
            setDeleteTemplate('');
            dataProvider('DELETE', 'message-template', {
                id
            }).then(res => {
                console.log('On Delete one template:', res);
                getTemplates();
            }).catch(error => {
                console.error('On Delete:', error);
            });
        }, 6000);
    }

    const onCloseCreateForm = (isSuccess = false) => {
        if (isSuccess) {
            getTemplates();
        }
        setShowCreateDialog(false);
    }

    const onCloseEditForm = (isSuccess = false) => {
        if (isSuccess) {
            getTemplates();
        }
        setShowCreateDialog(false);
        setCurrentEditTemplate(null);
    }

    const openEditTemplateForm = (index) => {
        const selectedTemplate = templates[index];
        if (selectedTemplate) {
            setCurrentEditTemplate(selectedTemplate);
        } else {
            // TODO: Out of range error
        }
    }

    return (
        <Fragment>
            <Box padding={3} bgcolor='#ffffff'>
                <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                    {
                        isCreate &&
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => setShowCreateDialog(true)}
                            className={classes.button}
                        >{translate(`resources.messages.createTemplate`)}<img src={icEmail} width={13} style={{ marginLeft: 5 }} /></Button>
                    }
                    <Box flex={1} display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center">
                        <Search placeholder={translate(`resources.messages.searchTemplates`)} onChangeText={setSearchText} getList={getTemplates} />
                    </Box>
                </Box>
                <Box pt={4} display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                    <Box display="flex" flexDirection="row" alignItems="center">
                        <Typography className={classes.textCountPagination}>{templates.length === 0?0:(pagination.page - 1) * pagination.perPage + 1}-{(pagination.page - 1) * pagination.perPage + templates.length} {translate(`resources.alarm.of`)} {totalItems}</Typography>
                        <img src={icChevronLeft} alt='' className={classes.btnIconOnly} style={{ marginLeft: 8 }} onClick={onPrevious} />
                        <img src={icChevronRight} alt='' className={classes.btnIconOnly} style={{ marginLeft: 4 }} onClick={onNext} />
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="center">
                        <img src={icRefresh} alt=''
                            className={classes.btnIconOnly}
                            onClick={onRefresh}
                            style={{ marginRight: 4, width: 16, height: 16, padding: 4 }}
                        />
                        <Typography className={classes.textCountPagination}>{translate(`resources.alarm.refresh`)}</Typography>
                    </Box>
                </Box>
            </Box>
            <Box mt={2.75}>
                <Snackbar
                    id='snackbar-delete-message'
                    show={deleteTemplate.length > 0}
                    message={deleteTemplate}
                    undo={onUndoDeleted}
                    onClose={() => setDeleteTemplate('')}
                />
                <TableTemplateHeader
                    showSelectedHeader={showSelectedHeader}
                    onChangeSortable={onChangeSortable}
                    order={order}
                    onCheckAll={onCheckAll}
                    onDelete={onDeleteMany}
                    checkall={checkall}
                    onCheck={onCheck}
                    isDelete={isDelete}
                />
                <Loading show={loading} />

                {
                    !loading &&
                    <Box style={{ maxHeight: "60vh", overflow: 'auto' }}>
                        {
                            templates.map((item, index) =>
                                <TableTemplateItem
                                    key={item.id + ''}
                                    item={item}
                                    onCheck={onCheck}
                                    onDelete={onDeleteOne}
                                    onEdit={() => openEditTemplateForm(index)}
                                    isDelete={isDelete}
                                    isUpdate={isUpdate}
                                />
                            )
                        }
                    </Box>
                }

                <TablePagination
                    currentPage={page}
                    pages={pages}
                    onChangePage={onChangePage}
                    onPrevious={onPrevious}
                    onNext={onNext}
                />
            </Box>
            <DialogCreateTemplate
                open={showCreateDialog}
                onClose={onCloseCreateForm}
            />
            <DialogEditTemplate
                open={currentEditTemplate !== null}
                onClose={onCloseEditForm}
                template={currentEditTemplate}
            />
        </Fragment>
    );
}

TabSend.propTypes = {

};

export default TabSend;
